import { CircularProgress, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { DUPLICATE_ALERT_KEY, MODALS, WELLNESS_PLAN_STATUSES, modalStorageKeys } from "../../config/params";
/* eslint-disable max-len */
import { Drawer, Logo } from "./DrawerGeneric.style";
import React, { FC, useEffect } from "react";
import { useAllServiceStatusStore, useCombinedAlertsDataStore } from "../../hooks";
import useWellnessPlansByUser, { filterPetsForModal } from "../../services/wellness-plans-by-user/wellness-plan-by-user.service";

import { CalendarPlusIcon } from "../Icons";
import CustomerSupportButton from "../CustomerSupportButton/CustomerSupportButton";
import HomeHospitalIndicator from "../HomeHospitalIndicator/HomeHospitalIndicator";
import { IUserItemResponse } from "../../services/user-detail/user-detail.interface";
import { Image } from "../CardGeneric/CardGeneric.style";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";
import { NavLink } from "react-router-dom";
import { StyledBookAVisitButton } from "../LayoutApp/LayoutApp.style";
import { defaultTheme } from "../../styles/muiTheme";
import { useAllServiceStatus } from "../../services/all-service-status/all-service-status.service";
import { useAppointmentStore } from "../../hooks/redux-hooks/appointment-store/appointment.store";
import { useGTM } from "../../hooks/useGTM";
import { useGetAlerts } from "../../services/get-alerts/get-alerts.service";
import { useModal } from "../../context/ModalContext";
import { useOverdueInvoiceStore } from "../../hooks/redux-hooks/overdue-invoice-store/overdue-invoice.store";
import { usePetList } from "../../services/pet-list/pet-list.service";
import { userActivityLogger } from "../../utils/mixpanel";

export interface IItem {
  label: string;
  icon: JSX.Element;
  activeIcon?: JSX.Element;
  to?: string;
  onClick?: () => void;
  hide?: boolean;
  isCardAdded?: boolean;
}

const MenuItem = (
  { item, isActive, open }: { item: IItem, isActive: boolean, open: boolean },
) => {
  const color = isActive ? defaultTheme.palette.error.main : defaultTheme.palette.white.main;
  const textColor = isActive ? "#262626": defaultTheme.palette.gray.dark;

  return (
    <ListItemButton className="min-h-[3rem] py-4 px-10"
      sx={{ justifyContent: open ? "initial" : "center" }}
    >
      <ListItemIcon className="!min-w-0 justify-center relative"
        sx={{ "mr": open ? 2 : "auto", color }} >
        {isActive ? item.activeIcon : item.icon}
        {item?.isCardAdded === false && <Image src="/assets/icons/Notification bubble.svg"
          className="absolute bottom-[0.625rem] right-[-0.5625rem] !w-4" sx={{ height: "unset" }} alt={"info"}/>}
      </ListItemIcon>
      <ListItemText
        primary={item.label}
        sx={{
          "m": 0,
          "opacity": open ? 1 : 0,
          "fontWeight": 300,
          "& .MuiListItemText-primary": {
            color: textColor,
            cursor: "pointer",
          },
        }}
      />
    </ListItemButton>
  );
};

const NavLinkItem = ({ item, open, isDisabled }: { item: IItem, open: boolean, isDisabled: boolean }) => {
  const handleNavigation = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (isDisabled) {
      event.preventDefault();
    }
  };

  return (
    <>
      {item.onClick ? (
        <div onClick={item.onClick}>
          <MenuItem
            key={item.to}
            isActive={false}
            item={item}
            open={open}
          />
        </div>
      ) : (
        <NavLink
          key={item.to}
          to={item.to as string}
          onClick={handleNavigation}
          // eslint-disable-next-line react/no-children-prop
          children={({ isActive }) => (
            <MenuItem isActive={isActive} item={item} open={open} />
          )}
        />
      )}
    </>
  );
};


export function markExpressEligibilityPets(petNames: string[]) {
  const stored = localStorage.getItem("expressEligibilityShown");
  const parsed: string[] = stored ? JSON.parse(stored) : [];

  const updated = [...new Set([...parsed, ...petNames])];

  localStorage.setItem("expressEligibilityShown", JSON.stringify(updated));
}

export function getPetsShownForExpressModal(): string[] {
  const stored = localStorage.getItem("expressEligibilityShown");

  return stored ? JSON.parse(stored) : [];
}

export function filterPetsForExpressModal(petNames: string[]): string[] {
  const shownPets = getPetsShownForExpressModal();

  return petNames.filter((name) => !shownPets.includes(name));
}

interface IDrawerGenericProps {
  open: boolean;
  itemsMenu: IItem[];
  user?: IUserItemResponse;
}

const DrawerGeneric: FC<IDrawerGenericProps> = ({
  open,
  itemsMenu,
  user,
}) => {
  const { dispatchEvent } = useGTM();
  const { openModal, modalView } = useModal();
  const { setAppointmentsForm, setSelectedPets } = useAppointmentStore();
  const { getAlerts } = useGetAlerts();
  const { getWellnessPlansByUser, data, loading: wellnessPlanDataLoading } = useWellnessPlansByUser();
  const { setCancelledPetList, setPausedPetList, setPendingPaymentPetList, setIsOverdueStoreUpdated, pendingPaymentPetList,
    isBookingBlocked, isOverdueStoreUpdated, pausedWellnessPetList, cancelledWellnessPetList, setCancelledPetListWithoutOverdue,
    setScratchpayOverdueNoticePetIds }=useOverdueInvoiceStore();
  const { petsActive }=usePetList();
  const { isServiceStatusStoreUpdated, setIsUrgentCareOnly }=useAllServiceStatusStore();
  const { getAllServicesStatus }=useAllServiceStatus();
  const {
    isDuplicateAccount,
    urgentVisitUpSell,
    isExpressEligible,
    updatePaymentMethod,
    alertsDataUpdated,
    failedRenewalsAfterGracePeriodWPData,
    endsSoonWPData,
    failedRenewalsWPData,
  } = useCombinedAlertsDataStore();


  const handleGoToBookAVisit = () => {
    if (isOverdueStoreUpdated) {
      userActivityLogger(MIXPANEL_EVENTS.BOOK_A_VISIT, { start_from: "left_nav",
        appt_type: "in_room_visit",
        overdue_status: pendingPaymentPetList.length > 0 ? "yes" : "no",
        is_plan_cancelled: cancelledWellnessPetList.length > 0 ? "true" : "false",
      });
      dispatchEvent({
        eventName: "gv_book_a_visit_button_sidebar",
      });
      setAppointmentsForm(null);
      setSelectedPets([]);
      if (petsActive?.length==1) {
        userActivityLogger(MIXPANEL_EVENTS.BOOK_A_VISIT_SELECT_PET, { no_of_pets: 1, pet_name: petsActive[0].name,
          owner_type: petsActive?.length==1?"single_pet":"multipet", referred_from: "left_nav", appt_type: "in_room_visit",
        });
        setSelectedPets(petsActive);
      }
      if (!isBookingBlocked && (pendingPaymentPetList.length > 0)) {
        const delinquencyState = cancelledWellnessPetList.length > 0 ? "cancelled" : "paused";
        userActivityLogger(MIXPANEL_EVENTS.DELINQUENCY_BOOKING_BLOCKED, {
          delinquency_state: delinquencyState,
          entry_point: "left_nav",
          num_affected_pets: pendingPaymentPetList.length,
        });
        if (cancelledWellnessPetList.length > 0) {
          openModal(MODALS.PENDING_INVOICE_CANCELLED_ALERT_MODAL);
        } else if (pausedWellnessPetList.length > 0) {
          openModal(MODALS.PENDING_INVOICE_PAUSED_ALERT_MODAL);
        } else {
          openModal(MODALS.GENERIC_PAYMENT_ISSUE_MODAL);
        }
      } else {
        openModal(MODALS.UPDATED_BOOK_A_VISIT_MODAL);
      }
    }
  };

  const getNumberOfRenewalAlerts = () => {
    const alertSources = [
      { plans: endsSoonWPData, key: modalStorageKeys.wellnessPlansEndingSoon },
      { plans: failedRenewalsAfterGracePeriodWPData, key: modalStorageKeys.autoRenewUnsuccessfulEndedPlans },
      { plans: failedRenewalsWPData, key: modalStorageKeys.autoRenewEndedPlans },
    ];

    return alertSources.reduce((count, { plans, key }) => {
      if (plans?.length && filterPetsForModal(key, plans.map((p) => p.petId))?.length) {
        return count + 1;
      }

      return count;
    }, 0);
  };

  useEffect(()=>{
    if (!isOverdueStoreUpdated) {
      getWellnessPlansByUser();
    }
    !isServiceStatusStoreUpdated && getAllServicesStatus();
    getAlerts();
  }, []);

  useEffect(()=>{
    if (!isServiceStatusStoreUpdated) {
      getAllServicesStatus();
    }
  }, [isServiceStatusStoreUpdated]);


  useEffect(() => {
    if (data && !isOverdueStoreUpdated) {
      if (data.length > 0) {
        const outstandingPaymentData=data.filter((petData) => {
          const status = petData.wellnessPlanPaymentStatus.toLowerCase();

          return !petData.isScratchpay && (
            status === WELLNESS_PLAN_STATUSES.PAUSED ||
            status === WELLNESS_PLAN_STATUSES.LOCKED ||
            status === WELLNESS_PLAN_STATUSES.CANCELLED && petData.pendingEzyvetInvoices !== null);
        });
        const pausedPetList = data.filter(
          (petData) =>
            (petData.wellnessPlanPaymentStatus.toLowerCase() === WELLNESS_PLAN_STATUSES.PAUSED ||
          petData.wellnessPlanPaymentStatus.toLowerCase() === WELLNESS_PLAN_STATUSES.LOCKED) && (petData.pendingSquareInvoices.length > 0),
        ).map((filteredData)=>({ petId: filteredData.pet.id, petName: filteredData.pet.name, publicUrl: filteredData.pendingSquareInvoices[0]?.publicUrl,
          dueAmount: filteredData.pendingSquareInvoices[0]?.amount,
          invoiceDueDate: filteredData.pendingSquareInvoices[0]?.dueDate, planName: filteredData.wellnessPlan?.wellnessplanName }));

        const cancelledPetList = data.filter((petData) => (petData.wellnessPlanPaymentStatus.toLowerCase() === WELLNESS_PLAN_STATUSES.CANCELLED) && (petData.pendingEzyvetInvoices !== null)).
          map((filteredData)=>({ petId: filteredData.pet.id, petName: filteredData.pet.name, planName: filteredData.wellnessPlan?.wellnessplanName }));

        setCancelledPetList(cancelledPetList);
        setPausedPetList(pausedPetList);
        setPendingPaymentPetList(outstandingPaymentData.map((filteredData)=>({
          petId: filteredData.pet.id,
          petName: filteredData.pet.name,
          endDate: filteredData.wellnessPlanDetails?.endDate,
          wellnessPlanPaymentStatus: filteredData.wellnessPlanPaymentStatus,
          petImage: filteredData.pet.image ?? undefined,
          pendingInvoices: filteredData.pendingSquareInvoices.length,
          dueAmount: filteredData.pendingSquareInvoices[0]?.amount,
          invoiceDueDate: filteredData.pendingSquareInvoices[0]?.dueDate,
          publicUrl: filteredData.pendingSquareInvoices[0]?.publicUrl,
        })));

        const scratchpayOverdueNoticePetIds = data
          .filter((petData) => Boolean(petData.isScratchpay) && Boolean(petData.isOverdue))
          .map((petData) => String(petData.pet.id));
        setScratchpayOverdueNoticePetIds(scratchpayOverdueNoticePetIds);
        // To Update the soc overdue icon in profile card when only cancelled pet list is present without overdue
        const cancelledPetListWithoutOverdue = data.filter((petData) => {
          const status = petData.wellnessPlanPaymentStatus.toLowerCase();
          return status === WELLNESS_PLAN_STATUSES.CANCELLED ||
            status === WELLNESS_PLAN_STATUSES.PAUSED ||
            status === WELLNESS_PLAN_STATUSES.LOCKED;
        }).map((filteredData)=>({ petId: filteredData.pet.id, petName: filteredData.pet.name, planName: filteredData.wellnessPlan?.wellnessplanName }));

        setCancelledPetListWithoutOverdue(cancelledPetListWithoutOverdue);
      }
      setIsOverdueStoreUpdated(true);
    }
  }, [data]);

  useEffect(() => {
    if (user?.location && alertsDataUpdated && isOverdueStoreUpdated && !modalView) {
      // Urgent visit only transition alert (P1)
      if (urgentVisitUpSell) {
        setIsUrgentCareOnly(true);
        openModal(MODALS.URGENT_CARE_TRANSITION_MODAL);

        return;
      }

      // Location inactive alert (P1)
      if (user.location.status !== "active") {
        openModal(MODALS.CALL_HOSPITAL_MODAL);

        return;
      }

      // Duplicate account alert (P2)
      if (isDuplicateAccount) {
        const isDuplicateModalShown = localStorage.getItem(DUPLICATE_ALERT_KEY);

        if (!isDuplicateModalShown) {
          localStorage.setItem(DUPLICATE_ALERT_KEY, "true");
          openModal(MODALS.DUPLICATE_ACCOUNT_ALERT_MODAL);

          return;
        }
      }

      // Update payment method alert (P3)
      if (updatePaymentMethod && updatePaymentMethod?.length > 0) {
        const hideUpsellUntilDate = localStorage.getItem("updateWlnsPlanNtfn");

        if (!(hideUpsellUntilDate && new Date() < new Date(hideUpsellUntilDate))) {
          openModal(MODALS.EXPIRED_PAYMENT_UPDATE_MODAL);

          return;
        }
      }

      // --------------WELLNESS ALERTS ---------start

      // Recently ended WP alert (P4)

      if (getNumberOfRenewalAlerts() === 1) {
        // Renewal failed after grace period (P4)
        if (failedRenewalsAfterGracePeriodWPData?.length && filterPetsForModal(modalStorageKeys.autoRenewUnsuccessfulEndedPlans, failedRenewalsAfterGracePeriodWPData.map((pet) => pet.petId))?.length) {
          openModal(MODALS.WELLNESS_PLAN_ENDED_ALERT, "autoRenew");

          return;
        }
        // TotalCare renewal upsell (P4)
        if (endsSoonWPData?.length && filterPetsForModal(modalStorageKeys.wellnessPlansEndingSoon, endsSoonWPData.map((pet) => pet.petId))?.length) {
          openModal(MODALS.WELLNESS_RENEWAL_UPSELL);

          return;
        }
        // Renewal failed (due to overdue invoice) alert (P4)

        if (failedRenewalsWPData?.length && filterPetsForModal(modalStorageKeys.autoRenewEndedPlans, failedRenewalsWPData.map((pet) => pet.petId))?.length) {
          openModal(MODALS.WELLNESS_PLAN_RENEW_UNSUCCESSFUL_ALERT);

          return;
        }
      } else if (getNumberOfRenewalAlerts() > 1) {
        openModal(MODALS.WELLNESS_PLAN_RENEWAL_REMINDERS);

        return;
      }

      // --------------WELLNESS ALERTS ---------end

      // Express eligibility alert (P5)
      if (isExpressEligible?.length > 0) {
        const filteredPets = filterPetsForExpressModal(isExpressEligible.map((pet: any) => pet?.petName));

        if (filteredPets?.length) {
          openModal(MODALS.EXPRESS_SERVICE_UPSELL_MODAL);

          return;
        }
      }
    }
  }, [
    user,
    isDuplicateAccount,
    urgentVisitUpSell,
    isExpressEligible,
    updatePaymentMethod,
    alertsDataUpdated,
    modalView,
    isOverdueStoreUpdated,
  ]);


  return (
    <Drawer variant="permanent" open={open}>
      <div className="h-full flex flex-col justify-between">
        <div>
          <Logo>
            {open ?
              <img src="/assets/icons/logo_black.svg" alt="GoodVets" width={160} /> :
              <img src="/assets/icons/logo_horizontal.svg" alt="GoodVets" width={41} />
            }
          </Logo>

          <List sx={{ p: 0, mb: "1.5rem" }}>
            <div className="flex flex-col gap-2">
              {itemsMenu.filter((e) => !e.hide).map((item, index) => (
                <NavLinkItem key={index} item={item} open={open} isDisabled={!isOverdueStoreUpdated} />
              ))}
            </div>
          </List>
        </div>

        <div className={`${!open && "flex flex-col gap-4 items-center"}`}>
          <HomeHospitalIndicator isOpenSideBar={open} user={user} />
          {!open && <CustomerSupportButton variant="square" />}

          <div className="flex justify-center items-center">
            <StyledBookAVisitButton
              isOpenSidebar={open}
              id="bookVisitButton"
              color="primary"
              size="small"
              disabled={(user && !isBookingBlocked && isOverdueStoreUpdated) ? false : true}
              onClick={handleGoToBookAVisit}
              className={open ? "flex-1" : "w-full"}>
              {open ? (wellnessPlanDataLoading && !isOverdueStoreUpdated) ? <CircularProgress color="inherit" size={20} /> : "Book Appointment" : <CalendarPlusIcon />}
            </StyledBookAVisitButton>

            {open && <CustomerSupportButton variant="round" sx={{ mb: 0.5, mr: 3 }} />}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default DrawerGeneric;
